import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { PublicContestStandings, PublicStandingRow } from '@/features/competition/types'
import { cn } from '@/lib/cn'

import type { ScreenWinner } from '../types'
import { Garland } from './festive'
import { CandidatePhoto } from './shared'

/**
 * La hauteur (en vh) que se partagent les lignes d'un tableau, une fois
 * l'en-tête de la page, le palmarès, le nom du concours et l'en-tête des
 * colonnes posés — mesurée sur une télé 16:9. Sans palmarès, la place
 * de la bande revient aux lignes.
 */
const ROWS_AREA_VH = 50
const BAND_VH = 14
/** L'espace entre deux lignes, en vh. */
const ROW_GAP_VH = 0.6
/** Une ligne ne dépasse pas cette hauteur : deux candidats ne font pas un mur. */
const MAX_ROW_VH = 10
/**
 * En dessous de cette hauteur de ligne (au-delà de dix candidats par
 * concours), le détail public / jury de chaque tour disparaît : la note du
 * tour reste lisible, plutôt que trois chiffres minuscules.
 */
const SPLIT_MIN_ROW_VH = 5.5
/** La largeur de référence d'une colonne : deux concours côte à côte. */
const TWO_COLUMNS_VW = 45.75

/**
 * Les scores publiés, à la télé : tout, sur une seule page.
 *
 * En tête, le palmarès sous une guirlande — « Vainqueur · Miss : N° 3 Aïcha »
 * pour chaque titre proclamé. Dessous, un tableau par concours, côte à côte :
 * chaque tour (sa note sur 100, avec la part du public et celle du jury) et
 * le total. Ni rotation, ni pagination : la salle lit la page entière
 * pendant que le MC commente. Plus de dix candidats par concours : le détail
 * public / jury se retire d'abord, puis les lignes rétrécissent.
 *
 * Tous les poids (70, 30, 100, 300) sont lus du serveur : changer la règle
 * ne demande pas de toucher à cet écran.
 */
export function ResultsBoard({
  contests,
  winners,
}: {
  contests: PublicContestStandings[]
  winners: ScreenWinner[]
}) {
  const shown = contests.filter((contest) => contest.rows.length > 0)
  const densest = Math.max(1, ...shown.map((contest) => contest.rows.length))
  const area = ROWS_AREA_VH + (winners.length > 0 ? 0 : BAND_VH)
  const rowHeight = Math.min(MAX_ROW_VH, (area - ROW_GAP_VH * (densest - 1)) / densest)
  const columnWidth = (94 - 2.5 * (shown.length - 1)) / Math.max(shown.length, 1)

  return (
    <div className="flex h-full min-h-0 flex-col gap-[2vh]">
      {winners.length > 0 ? <WinnersBand winners={winners} /> : null}
      <div
        className="grid min-h-0 flex-1 gap-x-[2.5vw]"
        style={{ gridTemplateColumns: `repeat(${Math.max(shown.length, 1)}, minmax(0, 1fr))` }}
      >
        {shown.map((contest) => (
          <ContestTable
            key={contest.competition_category_id}
            contest={contest}
            rowHeight={rowHeight}
            widthFactor={Math.min(columnWidth / TWO_COLUMNS_VW, 2)}
          />
        ))}
      </div>
    </div>
  )
}

/** Le palmarès en tête de page, sous une guirlande : un titre, une photo, un nom. */
function WinnersBand({ winners }: { winners: ScreenWinner[] }) {
  const { t } = useTranslation()
  const nameSize = winners.length > 2 ? 'min(4.4vh,2vw)' : 'min(5.6vh,3vw)'
  return (
    <section aria-label={t('screen.winners')} className="shrink-0">
      <Garland swags={14} className="max-h-[5vh]" />
      <ul className="mt-[0.6vh] flex min-w-0 justify-center gap-x-[4vw]">
        {winners.map((winner) => (
          <li
            key={winner.competition_category_id ?? winner.contest}
            className="flex min-w-0 items-center gap-[1.2vw]"
          >
            <div className="aspect-[3/4] h-[10vh] shrink-0 overflow-hidden rounded-lg border-2 border-champagne-300">
              <CandidatePhoto
                url={winner.photo_url ?? null}
                number={winner.number ?? null}
                name={winner.name}
                className="[&>span]:text-[min(5vh,3vw)]"
              />
            </div>
            <div className="min-w-0">
              <p className="truncate text-[min(2.6vh,1.5vw)] uppercase tracking-[0.16em] text-ember-300">
                {t('screen.winnerOf', { contest: winner.contest })}
              </p>
              <p
                className="truncate font-display leading-tight text-champagne-300"
                style={{ fontSize: nameSize }}
              >
                {winner.number !== null && winner.number !== undefined ? (
                  <span className="mr-[0.8vw] font-mono">
                    {t('screen.number', { number: winner.number })}
                  </span>
                ) : null}
                {winner.name}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function ContestTable({
  contest,
  rowHeight,
  widthFactor,
}: {
  contest: PublicContestStandings
  rowHeight: number
  /** 1 pour deux concours côte à côte ; moins pour trois, plus pour un seul. */
  widthFactor: number
}) {
  const { t, i18n } = useTranslation()
  const roundMax = contest.public_weight + contest.jury_weight
  const totalMax = roundMax * Math.max(contest.rounds.length, 1)
  const showSplit = rowHeight >= SPLIT_MIN_ROW_VH
  const number = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    [i18n.language],
  )
  // Une taille en `min(vh, vw)` : la hauteur de ligne fixe la taille, la
  // largeur de la colonne la plafonne (trois concours, ou un seul).
  const size = (vh: number, vw: number) => `min(${vh.toFixed(2)}vh, ${(vw * widthFactor).toFixed(2)}vw)`
  // Les colonnes : rang, candidat, un tour chacun, le total.
  // Le nom du candidat d'abord : c'est lui que la salle cherche.
  const columns = `minmax(0,0.75fr) minmax(0,2.9fr) ${contest.rounds
    .map(() => 'minmax(0,1.1fr)')
    .join(' ')} minmax(0,1.1fr)`
  // L'en-tête d'un tour passe sur deux lignes, mais un mot ne se coupe pas
  // au hasard d'une lettre (« Abendkleid / ung ») : la césure n'existe pas
  // dans tous les navigateurs de télé. On réduit donc la taille jusqu'à ce
  // que le mot le plus long tienne dans sa colonne (≈ 0,55 em par lettre).
  const longestWord = Math.max(
    6,
    ...contest.rounds.flatMap((round) => round.split(/\s+/)).map((word) => word.length),
  )
  const fractions = 0.75 + 2.9 + 1.1 * contest.rounds.length + 1.1
  const innerVw = TWO_COLUMNS_VW * widthFactor - 1.6 - 0.8 * (contest.rounds.length + 2)
  const roundColumnVw = (innerVw * 1.1) / fractions
  const roundHeaderSize = `min(1.9vh, ${(roundColumnVw / (longestWord * 0.55)).toFixed(2)}vw)`

  return (
    <section className="flex min-h-0 min-w-0 flex-col">
      <p className="shrink-0 truncate text-[min(2vh,1.2vw)] uppercase tracking-[0.18em] text-ember-300">
        {t('screen.resultsOverline')}
      </p>
      <header className="flex shrink-0 items-end justify-between gap-[1.5vw]">
        <h2
          className="min-w-0 shrink-0 truncate font-display leading-none text-champagne-300"
          style={{ fontSize: size(5, 2.8) }}
        >
          {contest.name}
        </h2>
        <p className="line-clamp-2 min-w-0 text-right text-[min(1.8vh,1.05vw)] leading-snug text-content-secondary">
          {t('screen.resultsRule', {
            max: roundMax,
            public: contest.public_weight,
            jury: contest.jury_weight,
            total: totalMax,
          })}
          {showSplit ? ` · ${t('screen.resultsLegend')}` : ''}
        </p>
      </header>

      <div
        className="mt-[1.2vh] grid shrink-0 items-end gap-x-[0.8vw] border-b border-line-subtle pb-[0.8vh] text-[min(1.9vh,1.1vw)] uppercase tracking-[0.06em] text-content-muted"
        style={{ gridTemplateColumns: columns }}
      >
        <span className="truncate">{t('screen.rank')}</span>
        <span className="truncate">{t('screen.candidate')}</span>
        {contest.rounds.map((round) => (
          // Le nom du tour sur deux lignes au besoin : « Tenue tradition… »
          // ne dit plus de quel tour il s'agit. Un mot trop long se coupe
          // à la césure (« Stadt-kleidung »), pas au hasard d'une lettre.
          <span key={round} className="min-w-0 text-right normal-case leading-tight tracking-normal">
            <span
              className="line-clamp-2 hyphens-auto break-words"
              style={{ fontSize: roundHeaderSize }}
            >
              {round}
            </span>
            <span className="text-content-muted">/ {roundMax}</span>
          </span>
        ))}
        <span className="text-right">
          {t('screen.total')} <span className="normal-case">/ {totalMax}</span>
        </span>
      </div>

      <ol className="mt-[0.8vh] flex min-h-0 flex-1 flex-col" style={{ gap: `${ROW_GAP_VH}vh` }}>
        {contest.rows.map((row) => (
          <ResultRow
            key={row.candidate_id}
            row={row}
            rounds={contest.rounds}
            columns={columns}
            rowHeight={rowHeight}
            showSplit={showSplit}
            size={size}
            format={(value) => number.format(value)}
          />
        ))}
      </ol>
    </section>
  )
}

function ResultRow({
  row,
  rounds,
  columns,
  rowHeight,
  showSplit,
  size,
  format,
}: {
  row: PublicStandingRow
  rounds: string[]
  columns: string
  rowHeight: number
  showSplit: boolean
  size: (vh: number, vw: number) => string
  format: (value: number) => string
}) {
  const { t } = useTranslation()
  // Les lignes se partagent la hauteur qui reste (flex) ; `rowHeight` n'en
  // est que le plafond et la base des tailles de texte : la page ne déborde
  // jamais, même quand les en-têtes de tours passent sur deux lignes.
  const split = size(Math.min(Math.max(rowHeight * 0.19, 1.2), 1.7), 1)
  // Lignes serrées (le détail P / J s'est retiré) : une seule ligne de
  // texte par candidat, qui peut donc occuper une plus grande part de la
  // hauteur. Le numéro passe devant le nom, la vignette est trop petite.
  const dense = !showSplit
  const tie = row.won_by_tiebreak
    ? t('screen.byTiebreak')
    : row.won_by_jury
      ? t('screen.byJury')
      : null
  return (
    <li
      className={cn(
        'grid min-h-0 flex-1 items-center gap-x-[0.8vw] overflow-hidden rounded-lg px-[0.8vh]',
        row.is_winner
          ? 'border-2 border-champagne-400 bg-[color-mix(in_srgb,var(--champagne-400)_15%,transparent)]'
          : 'border border-line-subtle',
      )}
      style={{ gridTemplateColumns: columns, maxHeight: `${rowHeight}vh` }}
    >
      <span
        className="flex min-w-0 items-center gap-[0.4vh] font-mono font-semibold tabular-nums text-content-primary"
        style={{ fontSize: size(rowHeight * (dense ? 0.5 : 0.42), 2.1) }}
      >
        {row.is_winner ? (
          <span role="img" aria-label={t('screen.winnerTitle')} className="text-[0.8em] leading-none">
            👑
          </span>
        ) : null}
        <span className="shrink-0">{row.rank}</span>
      </span>

      <div className="flex min-w-0 items-center gap-[1vh]">
        <div
          className="relative aspect-square shrink-0 overflow-hidden rounded-md"
          style={{ height: `${(rowHeight * 0.82).toFixed(2)}vh` }}
        >
          <CandidatePhoto
            url={row.photo_url}
            number={row.candidate_number}
            name={row.name}
            className="[&>span]:text-[min(3.4vh,2vw)]"
          />
          {/* Le numéro posé sur la vignette : la largeur de la colonne va
              au nom, que la salle cherche d'abord. Sans photo, la vignette
              montre déjà le numéro en grand. */}
          {row.candidate_number !== null && row.photo_url && !dense ? (
            <span
              className="absolute bottom-0 left-0 rounded-tr-md bg-black/75 px-[0.5vh] font-mono font-semibold leading-tight text-champagne-300"
              style={{ fontSize: size(Math.max(rowHeight * 0.2, 1.3), 1.1) }}
            >
              {t('screen.number', { number: row.candidate_number })}
            </span>
          ) : null}
        </div>
        <div className="min-w-0">
          <p
            className={cn(
              'truncate font-display leading-tight',
              row.is_winner ? 'text-champagne-300' : 'text-content-primary',
            )}
            style={{ fontSize: size(rowHeight * (dense ? 0.5 : 0.42), 1.85) }}
          >
            {dense && row.candidate_number !== null ? (
              <span className="mr-[0.6vh] font-mono text-[0.8em] text-champagne-300">
                {t('screen.number', { number: row.candidate_number })}
              </span>
            ) : null}
            {row.name}
            {dense && tie ? <span className="text-[0.7em] text-ember-300"> · {tie}</span> : null}
          </p>
          {!dense && tie ? (
            <p className="truncate text-ember-300" style={{ fontSize: split }}>
              {tie}
            </p>
          ) : null}
        </div>
      </div>

      {rounds.map((roundName, position) => {
        // Les tours d'une ligne suivent l'ordre des colonnes ; on les
        // retrouve par leur nom, au cas où un candidat en aurait moins.
        const score = row.rounds.find((entry) => entry.round_name === roundName) ?? row.rounds[position]
        return (
          <div key={roundName} className="min-w-0 text-right">
            {score && score.competed ? (
              <>
                <p
                  className="font-mono leading-none tabular-nums text-content-primary"
                  style={{ fontSize: size(rowHeight * (dense ? 0.5 : 0.38), 1.65) }}
                >
                  {format(score.score)}
                </p>
                {showSplit ? (
                  // Public et jury l'un sous l'autre : une colonne de tour
                  // reste étroite même avec quatre tours.
                  <p
                    className="mt-[0.3vh] font-mono leading-tight tabular-nums text-content-secondary"
                    style={{ fontSize: split }}
                  >
                    <span className="block truncate">
                      {t('screen.splitPublic', { value: format(score.public_score) })}
                    </span>
                    <span className="block truncate">
                      {t('screen.splitJury', { value: format(score.jury_score) })}
                    </span>
                  </p>
                ) : null}
              </>
            ) : (
              <p className="text-content-muted" style={{ fontSize: size(rowHeight * 0.3, 1.5) }}>
                —
              </p>
            )}
          </div>
        )
      })}

      <p
        className={cn(
          'text-right font-mono font-semibold leading-none tabular-nums',
          row.is_winner ? 'text-champagne-300' : 'text-content-primary',
        )}
        style={{ fontSize: size(rowHeight * (dense ? 0.55 : 0.46), 2.1) }}
      >
        {format(row.total)}
      </p>
    </li>
  )
}
